/**
 * Is this "visitor" a crawler or an automation harness rather than a person?
 *
 * 🔴 WHY THIS EXISTS. Measured 2026-09-05: roughly 85-90% of `labs_preview_viewed` was not human.
 * The guide-page block is two ordinary anchors, `/try` is `noindex` but perfectly crawlable, and
 * nothing in the tracking path drops bots, so Google renders both links on every one of ~700 pages.
 * Googlebot's user agent contains "Chrome", so it lands in the same `$browser` bucket as real
 * readers and cannot be filtered after the fact. Every open-denominator rate on the Experiment 4
 * board has been unusable because of it.
 *
 * 🔴 AND IT GETS WORSE WITH THE EMBED. Framed into the guide page, the lab is rendered by anything
 * that renders the article. Without this guard the embed's own numbers would be born unreadable,
 * which would make it impossible to tell whether removing the navigation helped.
 *
 * WHAT THIS IS NOT. It is not access control and must never be used as any. A determined client can
 * present any user agent it likes. It is a MEASUREMENT filter whose only job is keeping obvious
 * non-humans out of a funnel, and its only failure mode is a slightly smaller, cleaner denominator.
 */

/**
 * Substrings that appear in the user agent of things that are not people.
 *
 * Deliberately conservative. Every entry here is a token that a real browser's UA does not contain,
 * so the cost of a false positive (dropping a real reader's events) stays near zero. `bot` catches
 * Googlebot, Bingbot, Applebot, DuckDuckBot, Twitterbot and Slackbot in one; `headless` catches
 * HeadlessChrome, which is most scrapers and our own QA runs.
 */
const AUTOMATED_UA_TOKENS: readonly string[] = [
  'bot',
  'crawl',
  'spider',
  'slurp',
  'headless',
  'phantomjs',
  'puppeteer',
  'playwright',
  'lighthouse',
  'pagespeed',
  'preview',
  'archiver',
  'facebookexternalhit',
  'embedly',
  'python-requests',
  'curl/',
  'wget',
];

/**
 * Decide whether to record this client's behaviour.
 *
 * Takes its inputs rather than reading globals so the decision is testable without a fake browser.
 *
 * `navigator.webdriver` is the strong signal and standardised: it is true under Playwright, Selenium
 * and Puppeteer, which covers our own automated QA. The user agent scan is the weaker one and exists
 * for crawlers, which do not set webdriver.
 */
export function isAutomatedClient({ userAgent, webdriver }: { userAgent: string; webdriver: boolean }): boolean {
  if (webdriver) {
    return true;
  }

  const ua = userAgent.toLowerCase();

  return AUTOMATED_UA_TOKENS.some((token) => ua.includes(token));
}

/**
 * Read the current browser and decide.
 *
 * Returns FALSE during server rendering. That is the safe direction: the alternative would drop the
 * events of every real reader whose first event fires before hydration, to avoid recording a crawler
 * that we will catch on the client anyway.
 */
export function isAutomatedBrowser(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return isAutomatedClient({
    userAgent: navigator.userAgent ?? '',
    webdriver: navigator.webdriver === true,
  });
}
